var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Stack, Typography, styled } from '@mui/material';
import { groupBy } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RumEntityLabel } from '../../observability/rumEntity';
import { Autocomplete, MultiAutocomplete, NativeNumberInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, getUnassignedLOB, } from '../common';
import { FirstCell } from './annualTable';
var FirstCellLOB = styled(FirstCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.grey[400],
    });
});
var EmptyCellLOB = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.grey[400],
    });
});
export var HeadcountChangeType;
(function (HeadcountChangeType) {
    HeadcountChangeType["Reduce"] = "reduce";
    HeadcountChangeType["Increase"] = "increase";
})(HeadcountChangeType || (HeadcountChangeType = {}));
var LobHeaderRow = function (_a) {
    var lobName = _a.lobName;
    return (_jsxs(TableRow, { children: [_jsx(FirstCellLOB, { children: lobName }), Array.from({ length: 3 }).map(function (_ignored, index) { return (_jsx(EmptyCellLOB, {}, "".concat(lobName, "-").concat(index))); })] }));
};
export var HeadcountChangeTable = function () {
    var _a;
    var _b = useFormContext(), watch = _b.watch, setValue = _b.setValue;
    var t = useTranslation(['report', 'translation'], { keyPrefix: 'annual.headcountChange' }).t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var rows = (_a = watch('headcountChanges')) !== null && _a !== void 0 ? _a : [];
    var handleTypeChange = function (index, value) {
        setValue("headcountChanges.".concat(index, ".type"), value !== null && value !== void 0 ? value : HeadcountChangeType.Reduce, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };
    var handleHeadcountChange = function (index, value) {
        setValue("headcountChanges.".concat(index, ".headcount"), value, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };
    var handleLocationChange = function (index, queueLocationIds) {
        setValue("headcountChanges.".concat(index, ".queueLocationIds"), queueLocationIds.map(Number).filter(function (id) { return Number.isFinite(id); }), {
            shouldDirty: true,
            shouldValidate: true,
        });
    };
    var headcountLabelText = t('headcount');
    var typeLabelText = t('type');
    var locationLabelText = t('locations');
    var noLocationsAvailableText = t('noLocationsAvailable');
    var reduceLabelText = t('reduce');
    var increaseLabelText = t('increase');
    var unassignedLob = getUnassignedLOB();
    var groupedRows = useMemo(function () { return groupBy(rows, 'lobName'); }, [rows]);
    return (_jsxs(Stack, __assign({ gap: 4 }, { children: [_jsx(Typography, __assign({ variant: "body1" }, { children: t('description') })), _jsx(TableContainer, __assign({ topScroll: true }, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, __assign({ align: "left", sx: { minWidth: '20rem' } }, { children: t('chooseQueues') })), _jsx(TableCell, __assign({ sx: { minWidth: '8rem' } }, { children: headcountLabelText })), _jsx(TableCell, __assign({ sx: { minWidth: '8rem' } }, { children: typeLabelText })), _jsx(TableCell, __assign({ sx: { minWidth: '15rem' } }, { children: locationLabelText }))] }) }), _jsx(TableBody, { children: Object.entries(groupedRows).map(function (_a) {
                                var lobName = _a[0], lobRows = _a[1];
                                return (_jsxs(Fragment, { children: [_jsx(LobHeaderRow, { lobName: lobName || unassignedLob }), lobRows.map(function (row) {
                                            var _a, _b, _c;
                                            var rowIndex = rows.indexOf(row);
                                            var hasLocationOptions = ((_b = (_a = row.locationOptions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
                                            return (_jsxs(TableRow, { children: [_jsx(TableCell, __assign({ align: "left" }, { children: _jsx(RumEntityLabel, __assign({ kind: "queue", id: row.queueId > 0 ? row.queueId : undefined }, { children: row.queueName })) })), _jsx(TableCell, __assign({ padding: "none" }, { children: _jsx(NativeNumberInput, { value: (_c = row.headcount) !== null && _c !== void 0 ? _c : '', onChange: function (value) { return handleHeadcountChange(rowIndex, value); }, max: 100, min: 0, decimal: 1, fixedDecimalScale: true, suffix: "%" }) })), _jsx(TableCell, __assign({ padding: "none" }, { children: _jsx(Autocomplete, { options: [
                                                                { value: HeadcountChangeType.Reduce, label: reduceLabelText },
                                                                { value: HeadcountChangeType.Increase, label: increaseLabelText },
                                                            ], getOptionValue: function (o) { return o.value; }, getOptionLabel: function (o) { return o.label; }, value: row.type, onChange: function (value) { return handleTypeChange(rowIndex, value); } }) })), _jsx(TableCell, __assign({ padding: "none" }, { children: _jsx(MultiAutocomplete, { disabled: !hasLocationOptions, disablePortal: true, options: row.locationOptions, placeholder: hasLocationOptions ? undefined : noLocationsAvailableText, value: row.queueLocationIds, onChange: function (queueLocationIds) { return handleLocationChange(rowIndex, queueLocationIds); } }) }))] }, "".concat(row.queueId, "-").concat(row.queueName, "-").concat(rowIndex)));
                                        })] }, lobName));
                            }) })] }) }))] })));
};
